import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModalCategoria from './ModalCategoria'
import { categoriasService, type Categoria as CategoriaAPI, type FiltrosCategorias as FiltrosCategoriasAPI, type EstadisticasCategorias as EstadisticasCategoriasAPI } from '../../../services/categoriasService'
import { resolverPresentacionErrorApi } from '../../../services/apiError'
import { useToast } from '../../../ganchos/interfaz-usuario/usarNotificaciones'
import { usePermisos } from '../../../ganchos/usePermisos'
import type { CategoriaUI as Categoria } from './tipos'
import {
  FolderIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  TagIcon,
  DocumentIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  Squares2X2Icon,
  ListBulletIcon,
  FunnelIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircleIcon,
  ArchiveBoxIcon,
  BookOpenIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  CogIcon,
  PhotoIcon,
  VideoCameraIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'

interface FiltrosCategoriasUI {
  busqueda: string
  estado: string
  tipo: string
  nivel: string
  padre: string
}

const mapearCategoriaApiAUi = (categoria: CategoriaAPI): Categoria => ({
  ...categoria,
  subcategorias: (categoria.subcategorias ?? []).map(mapearCategoriaApiAUi),
  creadoPor:
    (categoria as Categoria & { creadoPor?: string }).creadoPor ??
    (categoria as Categoria & { createdBy?: string }).createdBy ??
    'Sistema',
})

const PaginaGestionCategorias: React.FC = () => {
  const { tienePermiso } = usePermisos()
  const { showToast } = useToast()
  const [categorias, setCategorias] = useState<Categoria[]>([])
  const [categoriasFiltradas, setCategoriasFiltradas] = useState<Categoria[]>([])
  const [cargando, setCargando] = useState(true)
  const [estadisticas, setEstadisticas] = useState<EstadisticasCategoriasAPI>({
    total: 0,
    principales: 0,
    subcategorias: 0,
    activas: 0,
    totalArchivos: 0
  })
  const [filtros, setFiltros] = useState<FiltrosCategoriasUI>({
    busqueda: '',
    estado: '',
    tipo: '',
    nivel: '',
    padre: ''
  })
  const [ordenamiento, setOrdenamiento] = useState<{campo: string, direccion: 'asc' | 'desc'}>({
    campo: 'orden',
    direccion: 'asc'
  })
  const [vistaActual, setVistaActual] = useState<'arbol' | 'lista'>('arbol')
  const [mostrarFiltros, setMostrarFiltros] = useState(false)
  const [categoriasExpandidas, setCategoriasExpandidas] = useState<Set<string>>(new Set())
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState<Categoria | null>(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [tipoModal, setTipoModal] = useState<'ver' | 'crear' | 'editar' | 'eliminar'>('ver')

  // Cargar datos
  useEffect(() => {
    const cargarCategorias = async () => {
      setCargando(true)
      try {
        // Cargar categorías desde el backend
        const categoriasData = (await categoriasService.obtenerArbolCategorias()).map(mapearCategoriaApiAUi)
        setCategorias(categoriasData)
        setCategoriasFiltradas(categoriasData)
        
        // Cargar estadísticas desde el backend
        const estadisticasData = await categoriasService.obtenerEstadisticas()
        setEstadisticas(estadisticasData)
      } catch (error) {
        console.error('Error al cargar categorías:', error)
        const presentacion = resolverPresentacionErrorApi(error, {
          tituloFallback: 'Error',
          mensajeFallback: 'No fue posible cargar las categorías.',
          titulosPorCodigo: {
            forbidden: 'Acceso denegado',
            unauthorized: 'Sesión no válida',
          },
        })
        showToast({
          type: 'error',
          title: presentacion.title,
          message: presentacion.message,
        })
        // En caso de error, usar datos vacíos
        setCategorias([])
        setCategoriasFiltradas([])
        setEstadisticas({
          total: 0,
          principales: 0,
          subcategorias: 0,
          activas: 0,
          totalArchivos: 0
        })
      } finally {
        setCargando(false)
      }
    }

    cargarCategorias()
  }, [])

  // Aplicar filtros y cargar datos filtrados desde el backend
  useEffect(() => {
    const aplicarFiltros = async () => {
      if (categorias.length === 0) return // No aplicar filtros si no hay datos cargados

      try {
        // Si hay filtros activos, hacer una nueva consulta al backend
        const hayFiltrosActivos = Object.values(filtros).some(valor => valor && valor.trim() !== '')
        
        if (hayFiltrosActivos) {
          const filtrosAPI: FiltrosCategoriasAPI = {
            busqueda: filtros.busqueda || undefined,
            estado: filtros.estado || undefined,
            tipo: filtros.tipo || undefined,
            nivel: filtros.nivel ? parseInt(filtros.nivel) : undefined,
            padre: filtros.padre || undefined,
          }
          
          const categoriasFiltradas = (await categoriasService.obtenerCategorias(filtrosAPI)).map(mapearCategoriaApiAUi)
          setCategoriasFiltradas(categoriasFiltradas)
        } else {
          // Si no hay filtros, usar todas las categorías
          setCategoriasFiltradas([...categorias])
        }
      } catch (error) {
        console.error('Error al aplicar filtros:', error)
        const presentacion = resolverPresentacionErrorApi(error, {
          tituloFallback: 'Error',
          mensajeFallback: 'No fue posible aplicar los filtros de categorías.',
        })
        showToast({
          type: 'error',
          title: presentacion.title,
          message: presentacion.message,
        })
        // En caso de error, filtrar localmente
        let categoriasFiltradas = [...categorias]

        if (filtros.busqueda) {
          const busqueda = filtros.busqueda.toLowerCase()
          categoriasFiltradas = categoriasFiltradas.filter(categoria =>
            categoria.nombre.toLowerCase().includes(busqueda) ||
            categoria.descripcion?.toLowerCase().includes(busqueda) ||
            categoria.metadatos?.keywords?.some(keyword => keyword.toLowerCase().includes(busqueda))
          )
        }

        if (filtros.estado) {
          categoriasFiltradas = categoriasFiltradas.filter(categoria => categoria.estado === filtros.estado)
        }

        if (filtros.tipo) {
          categoriasFiltradas = categoriasFiltradas.filter(categoria => categoria.metadatos?.tipo === filtros.tipo)
        }

        setCategoriasFiltradas(categoriasFiltradas)
      }
    }

    aplicarFiltros()
  }, [filtros, categorias])

  // Aplicar ordenamiento local
  useEffect(() => {
    const categoriasOrdenadas = [...categoriasFiltradas].sort((a, b) => {
      const valorA = a[ordenamiento.campo as keyof Categoria] as string | number
      const valorB = b[ordenamiento.campo as keyof Categoria] as string | number
      
      if (typeof valorA === 'string' && typeof valorB === 'string') {
        return ordenamiento.direccion === 'asc' 
          ? valorA.localeCompare(valorB)
          : valorB.localeCompare(valorA)
      } else {
        return ordenamiento.direccion === 'asc' 
          ? (valorA as number) - (valorB as number)
          : (valorB as number) - (valorA as number)
      }
    })

    setCategoriasFiltradas(categoriasOrdenadas)
  }, [ordenamiento])

  const obtenerIconoCategoria = (icono: string) => {
    const iconos: Record<string, JSX.Element> = {
      truck: <BriefcaseIcon className="h-5 w-5" />,
      book: <BookOpenIcon className="h-5 w-5" />,
      document: <DocumentTextIcon className="h-5 w-5" />,
      chart: <ArchiveBoxIcon className="h-5 w-5" />,
      users: <AcademicCapIcon className="h-5 w-5" />,
      academic: <AcademicCapIcon className="h-5 w-5" />,
      clipboard: <DocumentIcon className="h-5 w-5" />,
      cog: <CogIcon className="h-5 w-5" />,
      code: <DocumentTextIcon className="h-5 w-5" />,
      lightbulb: <PhotoIcon className="h-5 w-5" />,
      megaphone: <VideoCameraIcon className="h-5 w-5" />,
      archive: <ArchiveBoxIcon className="h-5 w-5" />
    }
    return iconos[icono] || <FolderIcon className="h-5 w-5" />
  }

  const obtenerColorEstado = (estado: string) => {
    switch (estado) {
      case 'activa':
        return 'bg-green-100 text-green-800'
      case 'inactiva':
        return 'bg-yellow-100 text-yellow-800'
      case 'archivada':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const obtenerColorTipo = (tipo?: string) => {
    switch (tipo) {
      case 'educativo':
        return 'bg-blue-100 text-blue-800'
      case 'administrativo':
        return 'bg-purple-100 text-purple-800'
      case 'operativo':
        return 'bg-green-100 text-green-800'
      case 'comercial':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const manejarOrdenamiento = (campo: string) => {
    setOrdenamiento(prev => ({
      campo,
      direccion: prev.campo === campo && prev.direccion === 'asc' ? 'desc' : 'asc'
    }))
  }

  const toggleExpansion = (categoriaId: string) => {
    setCategoriasExpandidas(prev => {
      const nuevas = new Set(prev)
      if (nuevas.has(categoriaId)) {
        nuevas.delete(categoriaId)
      } else {
        nuevas.add(categoriaId)
      }
      return nuevas
    })
  }

  const abrirModal = (tipo: typeof tipoModal, categoria?: Categoria) => {
    setTipoModal(tipo)
    setCategoriaSeleccionada(categoria || null)
    setModalVisible(true)
  }

  const cerrarModal = () => {
    setModalVisible(false)
    setCategoriaSeleccionada(null)
  }

  const manejarGuardarCategoria = async (categoriaData: Partial<Categoria>) => {
    try {
      if (tipoModal === 'crear') {
        await categoriasService.crearCategoria({
          nombre: categoriaData.nombre ?? '',
          descripcion: categoriaData.descripcion,
          color: categoriaData.color ?? '#3B82F6',
          icono: categoriaData.icono ?? 'folder',
          nivel: categoriaData.padre ? 1 : 0,
          padre: categoriaData.padre,
          estado: categoriaData.estado === 'inactiva' ? 'inactiva' : 'activa',
          tipo: categoriaData.padre ? 'subcategoria' : 'principal',
          orden: categoriaData.orden ?? Date.now(),
          subcategorias: [],
        })
        
        // Recargar el árbol de categorías
        const categoriasActualizadas = (await categoriasService.obtenerArbolCategorias()).map(mapearCategoriaApiAUi)
        setCategorias(categoriasActualizadas)
        setCategoriasFiltradas(categoriasActualizadas)
        
        // Recargar estadísticas
        const nuevasEstadisticas = await categoriasService.obtenerEstadisticas()
        setEstadisticas(nuevasEstadisticas)
        showToast({
          type: 'success',
          title: 'Categoría creada',
          message: 'La categoría se creó correctamente.',
        })
        
      } else if (tipoModal === 'editar' && categoriaSeleccionada) {
        await categoriasService.actualizarCategoria(categoriaSeleccionada.id, {
          nombre: categoriaData.nombre,
          descripcion: categoriaData.descripcion,
          color: categoriaData.color,
          icono: categoriaData.icono,
          nivel: categoriaData.padre ? 1 : 0,
          padre: categoriaData.padre,
          estado: categoriaData.estado === 'inactiva' ? 'inactiva' : 'activa',
          tipo: categoriaData.padre ? 'subcategoria' : 'principal',
          orden: categoriaData.orden,
        })
        
        // Recargar el árbol de categorías
        const categoriasActualizadas = (await categoriasService.obtenerArbolCategorias()).map(mapearCategoriaApiAUi)
        setCategorias(categoriasActualizadas)
        setCategoriasFiltradas(categoriasActualizadas)
        
        // Recargar estadísticas
        const nuevasEstadisticas = await categoriasService.obtenerEstadisticas()
        setEstadisticas(nuevasEstadisticas)
        showToast({
          type: 'success',
          title: 'Categoría actualizada',
          message: 'La categoría se actualizó correctamente.',
        })
      }
    } catch (error) {
      console.error('Error al guardar categoría:', error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'No fue posible guardar la categoría.',
        titulosPorCodigo: {
          validation_error: 'Datos inválidos',
          forbidden: 'Acceso denegado',
          not_found: 'Categoría no encontrada',
          conflict: 'Categoría duplicada',
        },
        mensajesPorCodigo: {
          validation_error: 'Verifica nombre, estado y metadatos antes de guardar la categoría.',
          forbidden: 'No tienes permisos para crear o editar categorías.',
          not_found: 'La categoría que intentas editar ya no existe.',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    }
  }

  const manejarEliminarCategoria = async (id: string) => {
    try {
      // Eliminar categoría usando el servicio
      await categoriasService.eliminarCategoria(id)
      
      // Recargar el árbol de categorías
      const categoriasActualizadas = (await categoriasService.obtenerArbolCategorias()).map(mapearCategoriaApiAUi)
      setCategorias(categoriasActualizadas)
      setCategoriasFiltradas(categoriasActualizadas)
      
      // Recargar estadísticas
      const nuevasEstadisticas = await categoriasService.obtenerEstadisticas()
      setEstadisticas(nuevasEstadisticas)
      showToast({
        type: 'success',
        title: 'Categoría eliminada',
        message: 'La categoría se eliminó correctamente.',
      })
    } catch (error) {
      console.error('Error al eliminar categoría:', error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'No fue posible eliminar la categoría.',
        titulosPorCodigo: {
          forbidden: 'Acceso denegado',
          not_found: 'Categoría no encontrada',
          conflict: 'Operación no permitida',
        },
        mensajesPorCodigo: {
          forbidden: 'No tienes permisos para eliminar categorías.',
          not_found: 'La categoría que intentas eliminar ya no existe.',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    }
  }

  const renderizarCategoriaArbol = (categoria: Categoria, nivel: number = 0) => {
    const tieneSubcategorias = categoria.subcategorias.length > 0
    const estaExpandida = categoriasExpandidas.has(categoria.id)

    return (
      <div key={categoria.id} className="mb-2">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:shadow-md transition-all duration-200 ${
            nivel > 0 ? 'ml-6' : ''
          }`}
          style={{ marginLeft: nivel * 24 }}
        >
          <div className="flex items-center flex-1">
            {tieneSubcategorias && (
              <button
                onClick={() => toggleExpansion(categoria.id)}
                aria-label={estaExpandida ? `Contraer ${categoria.nombre}` : `Expandir ${categoria.nombre}`}
                className="mr-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
              >
                {estaExpandida ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                )}
              </button>
            )}
            
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center mr-3"
              style={{ backgroundColor: categoria.color + '20', color: categoria.color }}
            >
              {obtenerIconoCategoria(categoria.icono)}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h3 className="font-medium text-gray-900 dark:text-white">{categoria.nombre}</h3>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorEstado(categoria.estado)}`}>
                  {categoria.estado}
                </span>
                {categoria.metadatos?.tipo && (
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorTipo(categoria.metadatos.tipo)}`}>
                    {categoria.metadatos.tipo}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{categoria.descripcion}</p>
              <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                <span>{categoria.totalArchivos} archivos</span>
                <span>Creado: {new Date(categoria.fechaCreacion).toLocaleDateString('es-ES')}</span>
              </div>
            </div>

            <div className="flex items-center space-x-2 ml-4">
              <button
                onClick={() => abrirModal('ver', categoria)}
                aria-label={`Ver categoría ${categoria.nombre}`}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <EyeIcon className="h-4 w-4" />
              </button>
              {tienePermiso('categorias:editar') && (
                <button
                  onClick={() => abrirModal('editar', categoria)}
                  aria-label={`Editar categoría ${categoria.nombre}`}
                  className="p-2 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
              {tienePermiso('categorias:eliminar') && (
                <button
                  onClick={() => abrirModal('eliminar', categoria)}
                  aria-label={`Eliminar categoría ${categoria.nombre}`}
                  className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </motion.div>
        
        {tieneSubcategorias && estaExpandida && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2"
          >
            {categoria.subcategorias.map(subcategoria => 
              renderizarCategoriaArbol(subcategoria, nivel + 1)
            )}
          </motion.div>
        )}
      </div>
    )
  }

  if (cargando) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Gestión de Categorías</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Organiza y administra las categorías del contenido educativo
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex space-x-3">
              <button
                onClick={() => setMostrarFiltros(!mostrarFiltros)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filtros
              </button>
              <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg">
                <button
                  onClick={() => setVistaActual('arbol')}
                  aria-label="Cambiar a vista de árbol"
                  className={`px-3 py-2 text-sm font-medium rounded-l-lg transition-colors duration-200 ${
                    vistaActual === 'arbol'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <Squares2X2Icon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setVistaActual('lista')}
                  aria-label="Cambiar a vista de lista"
                  className={`px-3 py-2 text-sm font-medium rounded-r-lg transition-colors duration-200 ${
                    vistaActual === 'lista'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <ListBulletIcon className="h-4 w-4" />
                </button>
              </div>
              {tienePermiso('categorias:crear') && (
                <button
                  onClick={() => abrirModal('crear')}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Nueva Categoría
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900">
                <FolderIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{estadisticas.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900">
                <TagIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Principales</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{estadisticas.principales}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900">
                <ChevronRightIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Subcategorías</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{estadisticas.subcategorias}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-yellow-100 dark:bg-yellow-900">
                <CheckCircleIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Activas</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{estadisticas.activas}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900">
                <DocumentIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Archivos</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{estadisticas.totalArchivos}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <AnimatePresence>
          {mostrarFiltros && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label htmlFor="categorias-filtro-busqueda" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Buscar
                  </label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      id="categorias-filtro-busqueda"
                      name="busqueda"
                      type="text"
                      placeholder="Nombre, descripción, keywords..."
                      value={filtros.busqueda}
                      onChange={(e) => setFiltros(prev => ({ ...prev, busqueda: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="categorias-filtro-estado" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Estado
                  </label>
                  <select
                    id="categorias-filtro-estado"
                    name="estado"
                    value={filtros.estado}
                    onChange={(e) => setFiltros(prev => ({ ...prev, estado: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Todos los estados</option>
                    <option value="activa">Activa</option>
                    <option value="inactiva">Inactiva</option>
                    <option value="archivada">Archivada</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="categorias-filtro-tipo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo
                  </label>
                  <select
                    id="categorias-filtro-tipo"
                    name="tipo"
                    value={filtros.tipo}
                    onChange={(e) => setFiltros(prev => ({ ...prev, tipo: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Todos los tipos</option>
                    <option value="educativo">Educativo</option>
                    <option value="administrativo">Administrativo</option>
                    <option value="operativo">Operativo</option>
                    <option value="comercial">Comercial</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="categorias-filtro-nivel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nivel
                  </label>
                  <select
                    id="categorias-filtro-nivel"
                    name="nivel"
                    value={filtros.nivel}
                    onChange={(e) => setFiltros(prev => ({ ...prev, nivel: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Todos los niveles</option>
                    <option value="0">Principal</option>
                    <option value="1">Subcategoría</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Lista de categorías */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Categorías ({categoriasFiltradas.length})
              </h2>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Vista: {vistaActual === 'arbol' ? 'Árbol' : 'Lista'}
              </div>
            </div>
          </div>

          <div className="p-6">
            {vistaActual === 'arbol' ? (
              <div className="space-y-4">
                {categoriasFiltradas.map(categoria => renderizarCategoriaArbol(categoria))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th
                        onClick={() => manejarOrdenamiento('nombre')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        <div className="flex items-center space-x-1">
                          <span>Categoría</span>
                          {ordenamiento.campo === 'nombre' && (
                            ordenamiento.direccion === 'asc' ? 
                            <ArrowUpIcon className="h-4 w-4" /> : 
                            <ArrowDownIcon className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Estado
                      </th>
                      <th
                        onClick={() => manejarOrdenamiento('totalArchivos')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        <div className="flex items-center space-x-1">
                          <span>Archivos</span>
                          {ordenamiento.campo === 'totalArchivos' && (
                            ordenamiento.direccion === 'asc' ? 
                            <ArrowUpIcon className="h-4 w-4" /> : 
                            <ArrowDownIcon className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => manejarOrdenamiento('fechaCreacion')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        <div className="flex items-center space-x-1">
                          <span>Fecha</span>
                          {ordenamiento.campo === 'fechaCreacion' && (
                            ordenamiento.direccion === 'asc' ? 
                            <ArrowUpIcon className="h-4 w-4" /> : 
                            <ArrowDownIcon className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {categoriasFiltradas.map((categoria, index) => (
                      <motion.tr
                        key={categoria.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center mr-3"
                              style={{ backgroundColor: categoria.color + '20', color: categoria.color }}
                            >
                              {obtenerIconoCategoria(categoria.icono)}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {categoria.nombre}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {categoria.descripcion}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {categoria.metadatos?.tipo && (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorTipo(categoria.metadatos.tipo)}`}>
                              {categoria.metadatos.tipo}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorEstado(categoria.estado)}`}>
                            {categoria.estado}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {categoria.totalArchivos}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div>{new Date(categoria.fechaCreacion).toLocaleDateString('es-ES')}</div>
                          <div className="text-xs">por {categoria.creadoPor || 'Sistema'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => abrirModal('ver', categoria)}
                              aria-label={`Ver categoría ${categoria.nombre}`}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            {tienePermiso('categorias:editar') && (
                              <button
                                onClick={() => abrirModal('editar', categoria)}
                                aria-label={`Editar categoría ${categoria.nombre}`}
                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                            {tienePermiso('categorias:eliminar') && (
                              <button
                                onClick={() => abrirModal('eliminar', categoria)}
                                aria-label={`Eliminar categoría ${categoria.nombre}`}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de categorías */}
      <ModalCategoria
        isOpen={modalVisible}
        onClose={cerrarModal}
        tipo={tipoModal}
        categoria={categoriaSeleccionada}
        categoriasPadre={categorias.filter(cat => cat.nivel === 0)}
        onGuardar={manejarGuardarCategoria}
        onEliminar={manejarEliminarCategoria}
      />
    </div>
  )
}

export default PaginaGestionCategorias
